import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { CmsImageSlideshow as CmsImage } from "../lib/images";
import { CmsText } from "../lib/texts";
import { api } from "../lib/api";
import { useI18n } from "../i18n";

/**
 * "Quiénes somos" — editorial minimal layout.
 * Cleaned up to match the rest of the site (no eyebrows, no metrics, no features grid).
 */
export default function About() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/quienes-somos" }).catch(() => {});
  }, []);

  const P1_ES = 'Xega es una empresa de tecnología conformada por personas con gran capacidad de "Mente de Obra" enfocados al desarrollo y comercialización de tecnologías innovadoras y de punta a nivel mundial para cubrir las necesidades locales e internacionales en cuanto a SEGURIDAD se refiere.';
  const P1_EN = 'Xega is a technology company built by people with great "Mind Power" capacity, focused on the development and commercialization of innovative, world-class technologies to cover local and international needs when it comes to SECURITY.';
  const P1_PT = 'A Xega é uma empresa de tecnologia formada por pessoas com grande capacidade de "Mente de Obra", focadas no desenvolvimento e na comercialização de tecnologias inovadoras e de ponta em nível mundial para atender às necessidades locais e internacionais no que diz respeito a SEGURANÇA.';

  const P2_ES = "Como parte de nuestra estrategia de negocio, establecemos relaciones estratégicas y comerciales sólidas con los líderes en la industria de seguridad, dando como resultado la integración de tecnología de punta con los más avanzados sistemas de seguridad — creando de esta forma soluciones robustas para las necesidades actuales y futuras.";
  const P2_EN = "As part of our business strategy, we build strong strategic and commercial relationships with the leaders of the security industry, integrating cutting-edge technology with the most advanced security systems — building robust solutions for today's needs and tomorrow's.";
  const P2_PT = "Como parte da nossa estratégia de negócio, estabelecemos relações estratégicas e comerciais sólidas com os líderes da indústria de segurança, integrando tecnologia de ponta com os sistemas de segurança mais avançados — criando soluções robustas para as necessidades atuais e futuras.";

  return (
    <div className="min-h-screen text-white" data-testid="about-page">
      <NavBar />

      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden h-[54vh] min-h-[400px] max-h-[620px] bg-[#06080D]">
        <CmsImage
          slotKey="about.hero"
          defaultSrc="/about/guard-radio.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080D] via-[#06080D]/70 to-[#06080D]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" />

        <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex items-center justify-center text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7, delay: .1 }}
            className="font-display text-[2.4rem] sm:text-5xl lg:text-[4.25rem] leading-[1.03] tracking-tight"
          >
            <CmsText
              slotKey="about.hero.title.v2"
              es="Somos <i>Xega</i>"
              en="We are <i>Xega</i>"
              pt="Somos <i>Xega</i>"
              lang={pick("es","en","pt")}
              className="chrome-text"
            />
          </motion.h1>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-[#06080D] pointer-events-none" />
      </section>

      {/* ═══════ EDITORIAL BLOCK #1 — Manifesto ═══════ */}
      <section className="relative pt-2 pb-2 bg-[#06080D]">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage
                  slotKey="about.control_room"
                  defaultSrc="/about/control-room.jpg"
                  alt={pick("Sala de control XEGA", "XEGA control room", "Sala de controle XEGA")}
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/50 via-transparent to-cyan-500/10 mix-blend-multiply" />
              </div>
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-7"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P1_ES, P1_EN, P1_PT)}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ EDITORIAL BLOCK #2 — Strategy ═══════ */}
      <section className="relative pt-0 pb-4 md:pb-8 bg-[#06080D]">
        <div className="absolute bottom-1/3 -right-40 w-[560px] h-[560px] rounded-full bg-emerald-400/6 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-7 order-2 lg:order-1"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P2_ES, P2_EN, P2_PT)}
              </p>

              {/* KPI ribbon */}
              <div className="mt-8 grid grid-cols-3 gap-3">
                {[
                  { k: "24/7",   v: pick("Operación", "Operation", "Operação") },
                  { k: "4",      v: pick("Sucursales LATAM", "LATAM branches", "Filiais LATAM") },
                  { k: "+25",    v: pick("Años de experiencia", "Years of experience", "Anos de experiência") },
                ].map((kpi, i) => (
                  <div key={i} className="glass-card chrome-border p-3 sm:p-4 text-center">
                    <div className="font-display text-2xl sm:text-3xl chrome-text">{kpi.k}</div>
                    <div className="label-mono !text-white/55 text-[9px] mt-1 leading-tight">{kpi.v}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-5 order-1 lg:order-2 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage
                  slotKey="about.tablet_map"
                  defaultSrc="/about/tablet-map.jpg"
                  alt={pick("Tecnología en tiempo real", "Real-time technology", "Tecnologia em tempo real")}
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tl from-[#06080D]/60 via-transparent to-emerald-500/10 mix-blend-multiply" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-cyan-400/40 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
